import { useCallback, useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';
import { slugify } from '../lib/slug';

export interface Generator {
    id: string;
    organization_id: string;
    name: string;
    slug: string;
    industry: string | null;
    industry_custom: string | null;
    config: Record<string, unknown>;
    is_published: boolean;
    created_at: string;
    updated_at: string;
}

interface UseGeneratorsResult {
    generators: Generator[];
    loading: boolean;
    error: string | null;
    refetch: () => Promise<void>;
    createGenerator: (payload: {
        name: string;
        industry: string;
        industryCustom?: string;
    }) => Promise<Generator>;
}

export function useGenerators(organizationId: string | null | undefined): UseGeneratorsResult {
    const [generators, setGenerators] = useState<Generator[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const fetchGenerators = useCallback(async () => {
        if (!organizationId) {
            setGenerators([]);
            setLoading(false);
            return;
        }
        setLoading(true);
        setError(null);

        const { data, error: err } = await supabase
            .from('generators')
            .select('*')
            .eq('organization_id', organizationId)
            .order('created_at', { ascending: false });

        if (err) {
            setError(err.message);
            setLoading(false);
            return;
        }

        setGenerators((data ?? []) as Generator[]);
        setLoading(false);
    }, [organizationId]);

    useEffect(() => {
        void fetchGenerators();
    }, [fetchGenerators]);

    const createGenerator = useCallback(
        async ({
            name,
            industry,
            industryCustom,
        }: {
            name: string;
            industry: string;
            industryCustom?: string;
        }): Promise<Generator> => {
            if (!organizationId) throw new Error('Aucune organisation');

            const slug = slugify(name);
            const trimmedCustom = industryCustom?.trim();
            const { data, error: err } = await supabase
                .from('generators')
                .insert({
                    organization_id: organizationId,
                    name,
                    slug,
                    industry,
                    industry_custom: trimmedCustom ? trimmedCustom : null,
                    config: {},
                })
                .select('*')
                .single();

            if (err) throw err;
            await fetchGenerators();
            return data as Generator;
        },
        [organizationId, fetchGenerators]
    );

    return { generators, loading, error, refetch: fetchGenerators, createGenerator };
}
